import { useQuery, useQueryClient } from "@tanstack/react-query";
import { endpoints } from "@/lib/api/endpoints";
import { apiJsonClient } from "@/lib/query/core/api-json-client";
import { usePrivateMutation as useMutation } from "@/lib/query/core/use-private-mutation";
import { storeControlsQueryKey } from "@/modules/stores/services/storeControls";

/**
 * A store's own paid-service limits. Mirrors the backend's
 * `GET|PUT /admin/paid-services/stores/:storeId/limits` (super admin only).
 * `null` = follow the platform; 0 = unlimited.
 */
export type StorePaidLimits = {
  routesDailyMax: number | null;
  routesMonthlyMax: number | null;
  whatsappMonthlyMax: number | null;
  geminiMonthlyMax: number | null;
};

export type StorePaidLimitsView = {
  storeId: string;
  limits: StorePaidLimits;
  platform: {
    routesDailyMax: number;
    routesEssentialsMonthlyCap: number;
    routesProMonthlyCap: number;
    whatsappMonthlyCap: number;
    geminiMonthlyCap: number;
  };
  usage: {
    routesToday: number;
    routesThisMonth: number;
    whatsappThisMonth: number;
    geminiThisMonth: number;
  };
  updatedAt: string | null;
  updatedByAdminId: string | null;
};

export const storePaidLimitsQueryKey = (storeId: string) =>
  ["admin", "store-paid-service-limits", storeId] as const;

export function useStorePaidLimitsQuery(storeId: string) {
  return useQuery({
    queryKey: storePaidLimitsQueryKey(storeId),
    queryFn: () => apiJsonClient<StorePaidLimitsView>(endpoints.admin.storePaidServiceLimits(storeId)),
    enabled: storeId.length > 0,
  });
}

/** Saves all four limits at once; the answer replaces the cached view. */
export function useSaveStorePaidLimitsMutation() {
  const queryClient = useQueryClient();
  const mutation = useMutation({
    mutationFn: ({ storeId, limits }: { storeId: string; limits: StorePaidLimits }) =>
      apiJsonClient<StorePaidLimitsView>(endpoints.admin.storePaidServiceLimits(storeId), {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(limits),
      }),
    onSuccess: (data, variables) => {
      queryClient.setQueryData(storePaidLimitsQueryKey(variables.storeId), data);
      // The change is audited: the Store status card's history shows it.
      void queryClient.invalidateQueries({ queryKey: storeControlsQueryKey(variables.storeId) });
    },
  });
  return { mutateAsync: mutation.mutateAsync, isPending: mutation.isPending };
}
